import { useState, useEffect, useRef } from "react";
import { API } from "../backend";
import axios from "axios";
import authHeader from "../services/auth-header";
import ButtonLoading from '../components/Loder/buttonLoading';
import FullscreenIcon from '@material-ui/icons/Fullscreen';

function Basic() {
  const [chatboxVisible, setChatboxVisible]   = useState(false);
  const [chatLog, setChatLog]                 = useState([]);
  const [userInput, setUserInput]             = useState("");
  const [isLoading, setIsLoading]             = useState(false);
  const [chatbotEnabled, setChatbotEnabled]   = useState(true);
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  // Tracks what kind of input the current step expects
  // "buttons" | "text" | "number"
  const [inputType, setInputType]             = useState("buttons");

  const containerRef = useRef(null);

  // ── Subscription gate ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAccess = async () => {
      try {
        setLoadingFeatures(true);
        const res = await axios.get(`${API}paymentsub/subscription/`, {
          headers: authHeader(),
        });
        if (res.data.success === 1 && res.data.data) {
          const plan = res.data.data.plan;
          setChatbotEnabled(!(plan && plan.ask_athena_ai === false));
        }
      } catch {
        setChatbotEnabled(true);
      } finally {
        setLoadingFeatures(false);
      }
    };
    fetchAccess();
  }, []);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scroll({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [chatLog]);

  // ── Message helpers ────────────────────────────────────────────────────────
  const addUserMessage = (text) => {
    setChatLog((prev) => [...prev, { sender: "user", text }]);
  };

  const addBotMessage = (text, choices = [], type = "buttons", table = null) => {
    setChatLog((prev) => [...prev, { sender: "bot", text, choices, table }]);
    setInputType(type);
    setIsLoading(false);
  };

  // ── API call ───────────────────────────────────────────────────────────────
  const sendToBot = async (payload) => {
    try {
      setIsLoading(true);
      const res = await axios.post(
        `${API}chat/chat_with_bot/`,
        { user_message: payload },
        { headers: { ...authHeader(), "Content-Type": "application/json" } },
      );
      const data = res.data;
      if (data.bot_reply !== undefined) {
        addBotMessage(
          data.bot_reply,
          data.choices    || [],
          data.input_type || "buttons",
          data.table      || null,
        );
      }
    } catch (err) {
      console.error("Chatbot error:", err);
      addBotMessage("Something went wrong. Please try again.", [], "buttons");
    }
  };

  // ── Open / close ───────────────────────────────────────────────────────────
  const toggleChatbox = () => {
    if (!chatbotEnabled) return;
    if (!chatboxVisible) {
      // Fresh open — reset log and kick off Step 1
      setChatLog([]);
      setInputType("buttons");
      sendToBot("");          // empty → backend returns Step 1
    }
    setChatboxVisible((prev) => !prev);
  };

  // ── Choice button click ────────────────────────────────────────────────────
  const handleChoiceClick = (choice) => {
    if (isLoading) return;
    // Show user selection in chat (skip "Start Over" label)
    if (choice.title !== "🔄 Start Over") {
      addUserMessage(choice.title);
    }
    sendToBot(choice.payload !== undefined ? choice.payload : choice.title);
  };

  // ── Free-text / number Enter key ──────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.keyCode !== 13) return;
    const message = userInput.trim();
    if (!message || isLoading) return;
    addUserMessage(message);
    sendToBot(message);
    setUserInput("");
  };

  // ── Clear (reset tree state on backend + wipe log) ────────────────────────
  const clearHistory = async () => {
    try {
      await axios.post(
        `${API}chat/clear_history/`,
        {},
        { headers: authHeader() },
      );
    } catch { /* ignore */ }
    setChatLog([]);
    setInputType("buttons");
    sendToBot("");            // restart Step 1
  };

  // ── Fullscreen toggle ──────────────────────────────────────────────────────
  const handleScreen = () => {
    const chatbox = document.querySelector(".chatbox");
    if (!document.fullscreenElement) {
      chatbox?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // ── Input placeholder ──────────────────────────────────────────────────────
  const getPlaceholder = () => {
    if (inputType === "number") return "Type a number and press Enter…";
    if (inputType === "text")   return "Type your answer and press Enter…";
    return "Select an option above…";
  };

  if (loadingFeatures) return null;

  return (
    <div>
      <div className="container containerm">
        {/* Floating icon */}
        <div
          className="chat-icon"
          onClick={toggleChatbox}
          style={{ cursor: chatbotEnabled ? "pointer" : "not-allowed" }}
        >
          <img
            src="/static/assets/img/curved-images/Ask_Athena_Icon-removebg-previews.png"
            alt="Ask Athena"
            style={{
              width: "100%",
              height: "100%",
              filter:  chatbotEnabled ? "none" : "grayscale(100%)",
              opacity: chatbotEnabled ? 1 : 0.6,
            }}
          />
        </div>

        {/* Chatbox */}
        {chatboxVisible && chatbotEnabled && (
          <div className="chatbox">

            {/* Header */}
            <div className="chatbox-header">
              Ask Athena

              {/* Reset button */}
              <span
                title="Start over"
                className="cursor-pointer"
                style={{ position: "absolute", right: "75px", top: "15px", fontSize: "12px", opacity: 0.85 }}
                onClick={clearHistory}
              >
                <i className="fa fa-refresh" />
              </span>

              {/* Fullscreen */}
              <span
                className="fullscreen-icon cursor-pointer"
                style={{ position: "absolute", right: "50px", top: "15px" }}
                onClick={handleScreen}
              >
                <i><FullscreenIcon /></i>
              </span>

              {/* Close */}
              <span className="close-icon cursor-pointer" onClick={toggleChatbox}>
                <i className="fa fa-times" />
              </span>
            </div>

            {/* Message area */}
            <div
              className="chatbox-body"
              ref={containerRef}
              style={{ overflowY: "auto", flex: 1 }}
            >
              {chatLog.map((msg, idx) => (
                <div key={idx}>
                  <p className={`chatbox-message ${msg.sender === "user" ? "sent" : "received"}`}>
                    {msg.text !== undefined && (
                      <span className="message-content" style={{ fontSize: "smaller" }}>
                        {msg.text.split("\n").map((line, i) => (
                          <span key={i}>{line}<br /></span>
                        ))}
                      </span>
                    )}
                  </p>

                  {/* Data table (e.g. late fee breakdown) */}
                  {msg.sender === "bot" && msg.table && (
                    <div style={{ overflowX: "auto", padding: "6px 10px 4px" }}>
                      <table style={{
                        borderCollapse: "collapse",
                        fontSize:       "11px",
                        width:          "100%",
                        minWidth:       "320px",
                      }}>
                        <thead>
                          <tr>
                            {msg.table.headers.map((h, i) => (
                              <th key={i} style={{
                                border:     "1px solid #c8dce8",
                                padding:    "5px 8px",
                                background: "#1a73a7",
                                color:      "#fff",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                textAlign:  i === 0 ? "left" : "right",
                              }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {msg.table.rows.map((row, ri) => (
                            <tr key={ri} style={{
                              background: ri % 2 === 0 ? "#f4f8fb" : "#ffffff",
                            }}>
                              {row.map((cell, ci) => (
                                <td key={ci} style={{
                                  border:    "1px solid #dce8f0",
                                  padding:   "4px 8px",
                                  textAlign: ci === 0 ? "left" : "right",
                                  whiteSpace: "nowrap",
                                }}>
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Choice buttons — rendered below the bot message bubble */}
                  {msg.sender === "bot" &&
                   msg.choices &&
                   msg.choices.length > 0 && (
                    <div
                      className="choice-buttons"
                      style={{
                        display:    "flex",
                        flexWrap:   "wrap",
                        gap:        "6px",
                        padding:    "0 10px 10px 10px",
                      }}
                    >
                      {msg.choices.map((choice, bIdx) => (
                        <button
                          key={bIdx}
                          onClick={() => handleChoiceClick(choice)}
                          disabled={isLoading}
                          style={{
                            padding:      "6px 14px",
                            borderRadius: "16px",
                            border:       "1.5px solid #1a73a7",
                            background:   "#fff",
                            color:        "#1a73a7",
                            fontSize:     "12px",
                            fontWeight:   "500",
                            cursor:       isLoading ? "not-allowed" : "pointer",
                            transition:   "all 0.2s",
                            opacity:      isLoading ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isLoading) {
                              e.target.style.background = "#1a73a7";
                              e.target.style.color      = "#fff";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "#fff";
                            e.target.style.color      = "#1a73a7";
                          }}
                        >
                          {choice.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <p className="chatbox-message received message-content" style={{ fontSize: "smaller" }}>
                  <ButtonLoading color="#808080" size={10} />
                </p>
              )}
            </div>

            {/* Input bar */}
            <div
              className="chatbox-controls"
              style={{ position: "absolute", bottom: 0, width: "100%" }}
            >
              <input
                type={inputType === "number" ? "number" : "text"}
                className="chatbox-input"
                placeholder={getPlaceholder()}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                style={{ opacity: isLoading ? 0.6 : 1 }}
              />
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

export default Basic;
